/**
 * standardNames.ts
 *
 * Client API service for Standard Names Dictionary.
 */
import { apiFetch } from '@/services/apiFetch'

export type StandardNameGroup = 'gỗ' | 'ván' | 'ngũ kim' | 'bao bì' | 'khác'

export interface StandardNameAlias {
  id: string
  standard_name_id: string
  alias: string
  alias_normalized: string
  note: string | null
  created_at: string
}

export interface StandardName {
  id: string
  group: StandardNameGroup
  standard_name: string
  default_uom: string | null
  spec_hint: string | null
  active: boolean
  created_at: string
  updated_at: string
  aliases: StandardNameAlias[]
  bom_usage_count: number
  quote_usage_count: number
}

export interface StandardNamesStats {
  totalNames: number
  totalAliases: number
  activeNames: number
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const res = await apiFetch(input, init)
  const data = await res.json()
  if (!res.ok) {
    const err = data as { error?: { message?: string } }
    throw new Error(err?.error?.message || `Lỗi HTTP ${res.status}`)
  }
  return data as T
}

export const standardNameService = {
  async list(params?: {
    group?: string
    search?: string
    activeOnly?: boolean
  }): Promise<{ items: StandardName[]; stats: StandardNamesStats }> {
    const q = new URLSearchParams()
    if (params?.group) q.set('group', params.group)
    if (params?.search) q.set('search', params.search)
    if (params?.activeOnly) q.set('activeOnly', 'true')

    const res = await fetchJson<{ ok: boolean; items: StandardName[]; stats: StandardNamesStats }>(
      `/api/standard-names${q.toString() ? `?${q.toString()}` : ''}`,
    )
    return { items: res.items, stats: res.stats }
  },

  async getById(id: string): Promise<StandardName> {
    const res = await fetchJson<{ ok: boolean; item: StandardName }>(`/api/standard-names/${id}`)
    return res.item
  },

  async create(data: {
    group: StandardNameGroup
    standard_name: string
    default_uom?: string
    spec_hint?: string
  }): Promise<StandardName> {
    const res = await fetchJson<{ ok: boolean; item: StandardName }>('/api/standard-names', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(data),
    })
    return res.item
  },

  async update(
    id: string,
    data: {
      group?: StandardNameGroup
      standard_name?: string
      default_uom?: string | null
      spec_hint?: string | null
      active?: boolean
    },
  ): Promise<StandardName> {
    const res = await fetchJson<{ ok: boolean; item: StandardName }>(`/api/standard-names/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(data),
    })
    return res.item
  },

  async addAlias(id: string, alias: string, note?: string): Promise<StandardNameAlias> {
    const res = await fetchJson<{ ok: boolean; alias: StandardNameAlias }>(
      `/api/standard-names/${id}/aliases`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ alias, note }),
      },
    )
    return res.alias
  },

  async deleteAlias(aliasId: string): Promise<void> {
    await apiFetch(`/api/standard-names/aliases/${aliasId}`, {
      method: 'DELETE',
    })
  },
}
